import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ts, P } from '../api';
import ProfileEditor from '../ProfileEditor';
import { Page, Section, Field, Select, Table, Td, Pill, Empty, useLoad, useTS, when } from '../ui';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const TIMEZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu'];

export default function TSSettings() {
  const session = useTS();
  return (
    <Page title="Settings">
      <MyProfile />
      {session.can(P.COMPANY) && <CompanyRules />}
      {session.can(P.COMPANY) && <SmsRegistration />}
      {session.can(P.COMPANY) && <StaffApp />}
      {session.can(P.COMPANY) && <StaffPolicy />}
      {(session.can(P.STAFF) || session.can(P.SUPERVISE)) && <ProblemReports />}
      <DoNotContact />
      {session.can(P.SUPERVISE) && <AuditLog />}
    </Page>
  );
}

function MyProfile() {
  const session = useTS();
  const [forward, setForward] = useState(session.member.forward_phone || '');
  const [pw, setPw] = useState({ current_password: '', new_password: '' });
  const save = async (body, ok) => {
    try { await ts.updateMe(body); await session.refresh(); toast.success(ok); } catch (e) { toast.error(e.message); }
  };
  return (
    <Section title="My profile">
      <div className="text-sm">{session.member.full_name} · {session.member.email}</div>
      <ProfileEditor member={session.member} onSaved={session.refresh} />
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Phone that rings for my calls" hint="Mobile or desk phone, any country, with country code.">
          <Input value={forward} onChange={e => setForward(e.target.value)} className="w-64" data-testid="ts-my-forward" />
        </Field>
        <Button size="sm" variant="outline" onClick={() => save({ forward_phone: forward }, 'Saved')}>Save</Button>
      </div>
      {session.role !== 'owner' ? (
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); save(pw, 'Password changed').then(() => setPw({ current_password: '', new_password: '' })); }}>
          <Field label="Current password"><Input type="password" value={pw.current_password} onChange={e => setPw({ ...pw, current_password: e.target.value })} data-testid="ts-pw-current" /></Field>
          <Field label="New password"><Input type="password" minLength={8} value={pw.new_password} onChange={e => setPw({ ...pw, new_password: e.target.value })} data-testid="ts-pw-new" /></Field>
          <Button size="sm" type="submit" data-testid="ts-pw-save">Change password</Button>
        </form>
      ) : <p className="text-xs text-muted-foreground">As the owner, you sign in with your Agent9 account password.</p>}
    </Section>
  );
}

function CompanyRules() {
  const session = useTS();
  const s = session.company.settings || {};
  const [form, setForm] = useState({
    timezone: s.timezone, business_hours: s.business_hours || {}, calling_window: s.calling_window || { start: '08:00', end: '21:00' },
    daily_call_limit_per_seat: s.daily_call_limit_per_seat, daily_sms_limit_per_seat: s.daily_sms_limit_per_seat,
    callback_rules: s.callback_rules || {}, follow_up_rules: s.follow_up_rules || {},
  });
  const day = (d, k, v) => setForm(f => ({ ...f, business_hours: { ...f.business_hours, [d]: { ...(f.business_hours[d] || {}), [k]: v } } }));
  const save = async () => {
    try {
      await ts.updateSection('settings', {
        ...form, daily_call_limit_per_seat: Number(form.daily_call_limit_per_seat), daily_sms_limit_per_seat: Number(form.daily_sms_limit_per_seat),
        callback_rules: { ...form.callback_rules, default_due_minutes: Number(form.callback_rules.default_due_minutes), reminder_minutes: Number(form.callback_rules.reminder_minutes) },
        follow_up_rules: { no_answer_retry_hours: Number(form.follow_up_rules.no_answer_retry_hours), max_attempts: Number(form.follow_up_rules.max_attempts) },
      });
      await session.refresh();
      toast.success('Saved');
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Section title="Business hours and calling rules" actions={<Button size="sm" onClick={save} data-testid="ts-rules-save">Save</Button>}>
      <Field label="Time zone"><Select value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })} options={TIMEZONES} /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {DAYS.map(d => {
          const h = form.business_hours[d] || {};
          return (
            <div key={d} className="flex items-center gap-2 text-sm">
              <label className="w-28 flex items-center gap-1 capitalize"><input type="checkbox" checked={!!h.enabled} onChange={e => day(d, 'enabled', e.target.checked)} /> {d}</label>
              <Input type="time" className="h-8 w-28" value={h.open || '09:00'} disabled={!h.enabled} onChange={e => day(d, 'open', e.target.value)} />
              <Input type="time" className="h-8 w-28" value={h.close || '17:00'} disabled={!h.enabled} onChange={e => day(d, 'close', e.target.value)} />
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Outbound calls allowed from (customer's time)"><Input type="time" value={form.calling_window.start} onChange={e => setForm({ ...form, calling_window: { ...form.calling_window, start: e.target.value } })} /></Field>
        <Field label="Until"><Input type="time" value={form.calling_window.end} onChange={e => setForm({ ...form, calling_window: { ...form.calling_window, end: e.target.value } })} /></Field>
        <Field label="Max calls per person per day"><Input type="number" value={form.daily_call_limit_per_seat} onChange={e => setForm({ ...form, daily_call_limit_per_seat: e.target.value })} /></Field>
        <Field label="Max texts per person per day"><Input type="number" value={form.daily_sms_limit_per_seat} onChange={e => setForm({ ...form, daily_sms_limit_per_seat: e.target.value })} /></Field>
        <Field label="Callback due in (minutes)"><Input type="number" value={form.callback_rules.default_due_minutes ?? 60} onChange={e => setForm({ ...form, callback_rules: { ...form.callback_rules, default_due_minutes: e.target.value } })} /></Field>
        <Field label="Remind before (minutes)"><Input type="number" value={form.callback_rules.reminder_minutes ?? 15} onChange={e => setForm({ ...form, callback_rules: { ...form.callback_rules, reminder_minutes: e.target.value } })} /></Field>
        <Field label="Retry no-answer after (hours)"><Input type="number" value={form.follow_up_rules.no_answer_retry_hours ?? 24} onChange={e => setForm({ ...form, follow_up_rules: { ...form.follow_up_rules, no_answer_retry_hours: e.target.value } })} /></Field>
        <Field label="Max attempts per lead"><Input type="number" value={form.follow_up_rules.max_attempts ?? 5} onChange={e => setForm({ ...form, follow_up_rules: { ...form.follow_up_rules, max_attempts: e.target.value } })} /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.callback_rules.missed_call_callback !== false} onChange={e => setForm({ ...form, callback_rules: { ...form.callback_rules, missed_call_callback: e.target.checked } })} />
        Create a callback for every missed call
      </label>
    </Section>
  );
}

const REG_FIELDS = [
  ['legal_name', 'Legal business name'], ['ein', 'EIN'], ['address_line1', 'Street address'], ['city', 'City'],
  ['state', 'State'], ['postal_code', 'ZIP code'], ['website', 'Website'],
];

function SmsRegistration() {
  const session = useTS();
  const { data, reload } = useLoad(() => ts.smsRegistration());
  const c = session.company;
  const [form, setForm] = useState({
    legal_name: c.legal_name || '', ein: c.verification?.ein || '', address_line1: c.address?.line1 || '', city: c.address?.city || '',
    state: c.address?.state || '', postal_code: c.address?.postal_code || '', website: c.website || '',
    use_case_description: '', sample_messages: '', opt_in_description: '',
  });
  const reg = data?.registration || {};
  const submit = async (e) => {
    e.preventDefault();
    try { await ts.submitSmsRegistration(form); toast.success('Submitted for review'); reload(); } catch (err) { toast.error(err.message); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Section title="Business texting (SMS registration)">
      <p className="text-xs text-muted-foreground">US carriers require every business that texts customers to register (10DLC). Calls work without it. Your numbers can text only after the carrier approves your company — we never mark texting active without the carrier's confirmation.</p>
      <div className="flex flex-wrap gap-2 items-center text-sm" data-testid="ts-sms-reg-status">
        Status: <Pill tone={reg.status === 'approved' ? 'green' : reg.status === 'rejected' ? 'red' : 'amber'}>{reg.status || 'not submitted'}</Pill>
        {data?.sms && Object.entries(data.sms).filter(([, v]) => typeof v === 'number').map(([k, v]) => <Pill key={k} tone="gray">{k.replace(/_/g, ' ')}: {v}</Pill>)}
        {reg.decision_reason && <span className="text-xs text-muted-foreground">{reg.decision_reason}</span>}
      </div>
      {reg.status !== 'approved' && (
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ts-sms-reg-form">
          {REG_FIELDS.map(([k, l]) => <Field key={k} label={l}><Input required value={form[k]} onChange={set(k)} /></Field>)}
          <Field label="What will you text customers about?"><Textarea required rows={2} value={form.use_case_description} onChange={set('use_case_description')} /></Field>
          <Field label="Two sample messages"><Textarea required rows={2} value={form.sample_messages} onChange={set('sample_messages')} /></Field>
          <Field label="How customers agree to receive texts"><Textarea required rows={2} value={form.opt_in_description} onChange={set('opt_in_description')} /></Field>
          <div className="sm:col-span-2 flex justify-end"><Button type="submit" size="sm">{reg.status ? 'Resubmit' : 'Submit'}</Button></div>
        </form>
      )}
    </Section>
  );
}

// Aster Phone office options: who may start group chats, read receipts,
// and whether lock-screen notifications show message text.
function StaffApp() {
  const session = useTS();
  const st = session.company?.settings || {};
  const [vals, setVals] = useState({
    staff_create_groups: !!st.chat?.staff_create_groups,
    read_receipts: st.chat?.read_receipts !== false,
    notification_previews: !!st.notifications?.previews,
  });
  const flip = async (key) => {
    const next = { ...vals, [key]: !vals[key] };
    try { await ts.staffAppSettings({ [key]: next[key] }); setVals(next); toast.success('Saved'); session.refresh(); }
    catch (e) { toast.error(e.message); }
  };
  const Row = ({ k, children }) => (
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" className="mt-1" checked={vals[k]} onChange={() => flip(k)} data-testid={`ts-staffapp-${k}`} />
      <span>{children}</span>
    </label>
  );
  return (
    <Section title="Aster Phone (staff app)">
      <Row k="staff_create_groups">Sales employees can create group chats (supervisors and administrators always can)</Row>
      <Row k="read_receipts">Show read receipts in office chats</Row>
      <Row k="notification_previews">Show message text on the lock screen (off: notifications only say "New message")</Row>
    </Section>
  );
}

// What staff may change for themselves in Aster Phone > Settings. The
// server enforces these; the app shows "Managed by your company".
const POLICY_ROWS = [
  ['allow_status_off_duty', 'Staff can set themselves Off Duty'],
  ['allow_job_title_edit', 'Staff can edit their own job title'],
  ['allow_self_device_revoke', 'Staff can sign out their other phones'],
  ['allow_personal_fallback', "Staff can have their own personal phone ring with the app (uses their own phone's minutes)"],
];
const POLICY_DEFAULTS = { allow_personal_fallback: false, allow_job_title_edit: true, allow_self_device_revoke: true, allow_status_off_duty: true };

function StaffPolicy() {
  const session = useTS();
  const [vals, setVals] = useState({ ...POLICY_DEFAULTS, ...(session.company?.settings?.staff_policy || {}) });
  const flip = async (key) => {
    const next = { ...vals, [key]: !vals[key] };
    setVals(next);
    try { await ts.updateSection('settings', { staff_policy: { [key]: next[key] } }); toast.success('Saved'); session.refresh(); }
    catch (e) { setVals(vals); toast.error(e.message); }
  };
  return (
    <Section title="What staff can change in their app">
      {POLICY_ROWS.map(([k, label]) => (
        <label key={k} className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={!!vals[k]} onChange={() => flip(k)} data-testid={`ts-policy-${k}`} />
          <span>{label}</span>
        </label>
      ))}
      <p className="text-xs text-muted-foreground">Name, extension, department, number, role, recording and routing are always set by you. Staff see them as "Managed by your company".</p>
    </Section>
  );
}

function ProblemReports() {
  const { data, reload } = useLoad(() => ts.supportReports());
  const close = async (r) => {
    try { await ts.closeSupportReport(r.id); reload(); toast.success('Closed'); } catch (e) { toast.error(e.message); }
  };
  const open = (data || []).filter(r => r.status === 'open').length;
  return (
    <Section title={`Problems reported from the staff app${open ? ` (${open} open)` : ''}`}>
      {(data || []).length === 0 ? <Empty>No problems reported.</Empty> : (
        <div className="max-h-96 overflow-y-auto" data-testid="ts-reports">
          <Table head={['When', 'Who', 'About', 'What happened', 'Phone', '']}>
            {data.map(r => (
              <tr key={r.id} data-testid={`ts-report-${r.id}`}>
                <Td>{when(r.created_at)}</Td>
                <Td>{r.member_name}</Td>
                <Td><Pill tone={r.status === 'open' ? 'amber' : 'gray'}>{r.category.replace('_', ' ')}</Pill></Td>
                <Td className="text-xs max-w-sm whitespace-pre-wrap break-words">{r.description}</Td>
                <Td className="text-xs">{[r.app_version && `v${r.app_version}`, r.device, r.diagnostics?.network].filter(Boolean).join(' · ')}{r.screenshot ? ' · screenshot' : ''}</Td>
                <Td className="text-right">{r.status === 'open' && <Button size="sm" variant="ghost" onClick={() => close(r)}>Close</Button>}</Td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </Section>
  );
}

function DoNotContact() {
  const session = useTS();
  const { data, reload } = useLoad(() => ts.suppression());
  const [form, setForm] = useState({ phone: '', channel: 'all', reason: 'Do not contact' });
  const add = async (e) => {
    e.preventDefault();
    try { await ts.suppress(form); setForm({ ...form, phone: '' }); reload(); toast.success('Added'); } catch (err) { toast.error(err.message); }
  };
  const remove = async (s) => {
    try { await ts.unsuppress(s.phone, s.channel); reload(); } catch (err) { toast.error(err.message); }
  };
  return (
    <Section title="Do-not-contact list">
      <form onSubmit={add} className="flex flex-wrap gap-2">
        <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" className="w-48 h-9" data-testid="ts-dnc-phone" />
        <Select value={form.channel} onChange={(channel) => setForm({ ...form, channel })} options={[['all', 'Calls and texts'], ['call', 'Calls'], ['sms', 'Texts']]} />
        <Button size="sm" type="submit" disabled={!form.phone} data-testid="ts-dnc-add">Add</Button>
      </form>
      <p className="text-xs text-muted-foreground">Customers who text STOP are added automatically. Numbers here are never called or texted from campaigns or by staff.</p>
      {(data || []).length === 0 ? <Empty>The list is empty.</Empty> : (
        <Table head={['Number', 'Channel', 'Reason', 'Added', '']}>
          {data.map(s => (
            <tr key={`${s.phone}-${s.channel}`}>
              <Td>{s.phone}</Td><Td>{s.channel}</Td><Td>{s.reason} <span className="text-xs text-muted-foreground">({s.source})</span></Td><Td>{when(s.created_at)}</Td>
              <Td className="text-right">{session.can(P.COMPLIANCE) && <Button size="sm" variant="ghost" onClick={() => remove(s)}>Remove</Button>}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Section>
  );
}

function AuditLog() {
  const { data } = useLoad(() => ts.audit());
  return (
    <Section title="Audit log">
      {(data || []).length === 0 ? <Empty>No entries.</Empty> : (
        <div className="max-h-80 overflow-y-auto">
          <Table head={['When', 'Who', 'What', 'Details']}>
            {data.map((a, i) => <tr key={a.id || i}><Td>{when(a.created_at)}</Td><Td>{a.actor_name || a.actor_id}</Td><Td>{a.action}</Td><Td className="text-xs max-w-sm break-words">{JSON.stringify(a.detail || {})}</Td></tr>)}
          </Table>
        </div>
      )}
    </Section>
  );
}
